import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  ErrorInfo,
  Grid,
  SectionHeader,
  TextFieldForm,
  Typography,
} from 'web-library';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import ControlledCustomSelect from '../../../components/form-items/ControlledCustomSelect';
import FormHeader from '../../../components/form-items/FormHeader';
import { ContactNumberFormItem } from '../../../components/form-items/ContactNumberFormItem';
import { useSetPasswordForm } from '../logic/useSetPasswordForm';
import { useManageRoute } from '../logic/useActivateAccount';
import RouteKey from '../../../routes/key';
import { typographyTheme } from 'web-library/theme/helper/tssHelper';

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  responsiveFlexContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  form: {},
  formElementContainer: {
    marginBottom: theme.spacing(4),
  },
  countryCodeSelect: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(1),
  },
  contactNumberInput: {},
  groupMultirowContainer: {
    marginBottom: theme.spacing(1),
    backgroundColor: 'rgba(0,0,0,0.2)', // TODO: get token from UI
  },
  groupSingleRowContainer: {
    display: 'flex',
    displayDirection: 'row',
  },
  IdTypeContainer: {
    marginRight: 5,
  },
  NricFinPassportInput: {
    flex: 1,
  },
  singleRowLeftItemContainer: {
    marginRight: theme.spacing(1),
  },
  contactNumberContainer: {
    flex: 1,
  },
  submitButton: {},
  label: { ...typographyTheme(theme, 'subtitle2') },
  groupHeaderLabel: {
    marginBottom: 10,
  },
  submitButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  formControl: {
    marginBottom: theme.spacing(4),
  },
  firstColInput: {
    paddingRight: theme.spacing(3),
  },
  approveInfo: {
    color: theme.color.text.textSecondary,
    marginTop: theme.spacing(1),
  },
  actionButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginRight: 0,
      marginBottom: theme.spacing(2),
    },
  },
  errorAsterisk: {
    color: theme.palette.error.main,
  },
  requiredInformationDescriptionContainer: {
    marginBottom: theme.spacing(3),
  },
}));

const RequiredIndicator = () => {
  const { classes } = useStyles();
  return <span className={classes.errorAsterisk}>&nbsp;*</span>;
};

const { auth: authRoute } = RouteKey.child;
const { activateAccount } = RouteKey.auth.child;

const SetPassword = ({ back }: { back: () => void }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const location = useLocation<any>();
  const accountData = location.state?.accountData;

  const { form, setPassword, submitError, setPasswordMutation } = useSetPasswordForm(
    accountData?.resetLink || ''
  );
  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = form;

  const history = useHistory();
  const { url } = useRouteMatch();
  const { localRouteKey } = useManageRoute(url);

  if (!accountData) {
    history.push(`/${authRoute}/${activateAccount}/${localRouteKey.detail}`);
  }

  return (
    <form
      onSubmit={handleSubmit((formData: any) => {
        setPassword(formData);
      })}
    >
      <ContentTemplate isDividerBottom>
        <SectionHeader title={t('admin->>SetPassword.Set Password')} />

        <Typography variant="body1">
          Use 12 or more characters with at least two of the following
        </Typography>
        <ul>
          <li>Lowercase</li>
          <li>Uppercase</li>
          <li>Digits</li>
          <li>Special Characters</li>
        </ul>
        <TextFieldForm
          form={form}
          label={
            <>
              {t('admin->>SetPassword.Enter a Password')}
              <RequiredIndicator />
            </>
          }
          name="setPassword"
          placeholder="Enter a password"
          type="password"
          error={!!errors.setPassword}
          helperText={errors?.setPassword?.message?.toString()}
          className={classes.formElementContainer}
          fullWidth
          // EPPOPNKHJV-1757: moved isInputTextFree from email input to password input
          isInputTextFree
        />
        <TextFieldForm
          form={form}
          label={
            <>
              {t('admin->>SetPassword.Confirm password')}
              <RequiredIndicator />
            </>
          }
          name="confirmPassword"
          placeholder="Confirm password"
          type="password"
          error={!!errors.confirmPassword}
          helperText={errors?.confirmPassword?.message?.toString()}
          className={classes.formElementContainer}
          fullWidth
          isInputTextFree
        />
      </ContentTemplate>
      <ContentTemplate continuous>
        <FormHeader title={t('admin->>SetPassword.TwoFAHeader')} />
        <Typography variant="body1" className={classes.requiredInformationDescriptionContainer}>
          {t('admin->>SetPassword.TwoFAHeadDescription')}
        </Typography>
        <TextFieldForm
          form={form}
          label={
            <>
              {t('admin->>SetPassword.Email')}
              <RequiredIndicator />
            </>
          }
          name="email"
          placeholder="Enter email address"
          type="text"
          error={!!errors.email}
          helperText={errors?.email?.message?.toString()}
          className={classes.formElementContainer}
          fullWidth
          defaultValue={accountData?.email || ''}
        />
        <ContactNumberFormItem
          containerClassName={classes.formElementContainer}
          formHook={form}
          label={
            <>
              {t('profile->>SetPassword.Mobile Number')}
              <RequiredIndicator />
            </>
          }
          countryCode={{
            name: 'countryCode',
            error: !!errors.countryCode,
            helperText: errors?.countryCode?.message?.toString(),
            disabled: true,
          }}
          number={{
            name: 'number',
            error: !!errors.number,
            helperText: errors?.number?.message?.toString(),
          }}
          numberDefaultValue={accountData?.number ? accountData.number : undefined}
          countryCodeDefaultValue={accountData?.countryCode ? accountData.countryCode : undefined}
        />
        <Typography variant="body1" className={classes.requiredInformationDescriptionContainer}>
          {t('admin->>SetPassword.SelectPreferred')}
        </Typography>
        <Box className={classes.formElementContainer}>
          <ControlledCustomSelect
            id="inputSelect-accountType"
            labelId="inputSelectLabel-accountType"
            label={
              <>
                {t('admin->>SetPassword.2FA Method')}
                <RequiredIndicator />
              </>
            }
            name="otpType"
            control={control}
            menuItems={[
              { key: 'S', value: 'SMS', children: 'SMS' },
              { key: 'E', value: 'EMAIL', children: 'EMAIL' },
            ]}
            variant="filled"
            defaultValue="SMS"
          />
        </Box>

        <Grid container direction="row" justify="flex-start">
          <Button variant="outlined" className={classes.actionButton} onClick={back}>
            {t('admin->>SetPassword.Back')}
          </Button>

          <ApiButton
            className={classes.actionButton}
            status={setPasswordMutation.status}
            type="submit"
          >
            {t('admin->>SetPassword.Activate Account')}
          </ApiButton>
        </Grid>
        <ErrorInfo label={submitError || ''} />
      </ContentTemplate>
    </form>
  );
};

export default SetPassword;
